import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import Sidebar from './Sidebar'
import Header from './Header'
import BottomNavigation from './BottomNavigation'
import OfflineIndicator from '../offline/OfflineIndicator'
import { useIsMobile } from '../../hooks/useMediaQuery'
import { useOfflineStatus } from '../../hooks/useOfflineStatus'

interface LayoutProps {
  children: React.ReactNode
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation()
  const isFloorPlanV2 = location.pathname.startsWith('/floor-plan')
  const isTimeline = location.pathname.startsWith('/timeline')

  // Default: collapsed (closed) sidebar on desktop/tablet
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true)
  const isMobile = useIsMobile();
  const { isOnline } = useOfflineStatus();

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed)
  }

  if (isMobile) {
    // Mobile layout with bottom navigation (Header removed to avoid scroll issues)
    return (
      <div className="flex flex-col h-full mobile-layout-full-height bg-background overflow-hidden">
        {/* Offline Indicator */}
        {!isOnline && <OfflineIndicator />}
        
        {/* Page content with extra bottom padding so last section is fully visible above nav */}
        <main 
          className="flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain p-4 min-h-0 min-w-0 w-full"
          style={{ 
            paddingBottom: 'max(10rem, calc(7rem + env(safe-area-inset-bottom, 3rem)))',
            WebkitOverflowScrolling: 'touch'
          }}
        >
          <div className="mobile-container pb-4">
            {children}
          </div>
        </main>
        
        {/* Bottom Navigation */}
        <BottomNavigation />
      </div>
    );
  }

  // Desktop/Tablet layout with sidebar
  return (
    <div className="flex h-screen bg-background overflow-hidden">
      {/* Sidebar */}
      <Sidebar collapsed={sidebarCollapsed} onToggle={toggleSidebar} />
      
      {/* Main content area */}
      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-300">
        {/* Offline Indicator */}
        {!isOnline && <OfflineIndicator showDetails />}
        
        {/* Header (hidden for immersive experiences like FloorPlan V2 and Timeline) */}
        {!isFloorPlanV2 && !isTimeline && <Header />}
        
        {/* Page content */}
        <main
          className={
            isFloorPlanV2 || isTimeline
              ? 'flex-1 overflow-hidden p-0 min-h-0'
              : 'flex-1 overflow-auto p-6 min-h-0'
          }
        >
          <div
            className={
              isFloorPlanV2 || isTimeline
                ? 'h-full min-h-0 max-w-none px-0'
                : 'tablet-container h-full min-h-0'
            }
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  )
}

export default Layout 